import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  JjamesButtonComponent,
  JJamesCardComponent,
  JJamesAlertComponent,
  PlateComponent,
  PLATE_COUNTRIES,
  normalizePlate,
  Role,
  JJamesPaginatorComponent,
  JJamesPaginationConfig
} from 'shared-lib';
import {
  LicensePlateService,
  ParkingRightService,
  ParkingProcessService,
  OrganizationService,
  RegisteredService,
  ParkingSpaceService,
  ParkerSiteClassificationService,
  WalletService,
  LicensePlateWalletsDto,
  LicensePlateDto,
  LicensePlateOwnershipDto,
  LicensePlateHolderDto,
  ParkingRightDto,
  ParkingProcessListItemDto,
  OrganizationDto,
  RegisteredParkerDto,
  ParkingSpaceDto,
  ParkerSiteClassificationDto,
  LicensePlateType,
  ParkingTypeEnum,
  ReassignLicensePlateOwnerResult,
  SortOrder
} from '../../api/gen';
import { RoleService } from '../auth/role-service';
import { LicensePlateBalanceComponent } from './license-plate-balance/license-plate-balance.component';
import { ParkerPoolCapComponent } from './parker-pool-cap/parker-pool-cap.component';
import { ParkerPoolCapStore } from './parker-pool-cap/parker-pool-cap.store';

export type LpDashboardTab = 'parkers' | 'rights' | 'transactions' | 'organizations' | 'balance';

/**
 * 360-Degree Multi-Schema License Plates Management.
 *
 * Provides comprehensive visibility and management across Parkers, Parking Rights,
 * Transactions (Stays), and Organizations while strictly enforcing operator space/zone
 * authorization rules.
 */
@Component({
  selector: 'app-license-plate-ownership',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    JJamesCardComponent,
    PlateComponent,
    JJamesPaginatorComponent,
    TranslatePipe,
    LicensePlateBalanceComponent,
    ParkerPoolCapComponent
  ],
  providers: [ParkerPoolCapStore],
  templateUrl: './license-plate-ownership.component.html',
  styleUrl: './license-plate-ownership.component.scss'
})
export class LicensePlateOwnershipComponent implements OnInit {

  private readonly plateApi = inject(LicensePlateService);
  private readonly rightApi = inject(ParkingRightService);
  private readonly processApi = inject(ParkingProcessService);
  private readonly orgApi = inject(OrganizationService);
  private readonly parkerApi = inject(RegisteredService);
  private readonly spaceApi = inject(ParkingSpaceService);
  private readonly classificationApi = inject(ParkerSiteClassificationService);
  private readonly walletApi = inject(WalletService);
  private readonly roleService = inject(RoleService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly fb = inject(FormBuilder);
  private readonly poolCapStore = inject(ParkerPoolCapStore);

  // ── Authorization & Accessible Scope ─────────────────────────────────────
  isAdministrator = signal(false);
  accessibleSpaces = signal<ParkingSpaceDto[]>([]);
  accessibleSpaceIds = computed(() => new Set(this.accessibleSpaces().map(s => s.id).filter((id): id is string => !!id)));

  // ── Search State ──────────────────────────────────────────────────────────
  plateQuery = signal('');
  plateResults = signal<LicensePlateDto[]>([]);
  isSearchingPlates = signal(false);
  plateSearched = signal(false);

  // ── Browse All Plates State ───────────────────────────────────────────────
  browsePlates = signal<LicensePlateDto[]>([]);
  browseTotal = signal(0);
  browsePageIndex = signal(0);
  browsePageSize = signal(15);
  isBrowseLoading = signal(false);

  // ── Selected Plate & 360° Enriched Data ───────────────────────────────────
  selectedPlate = signal<LicensePlateDto | null>(null);
  loadingEnriched = signal(false);
  activeTab = signal<LpDashboardTab>('parkers');

  // Enriched Schemas
  ownership = signal<LicensePlateOwnershipDto | null>(null);

  /** Parker credit for the selected plate (holders' wallets + what former owners earned on it). */
  wallets = signal<LicensePlateWalletsDto | null>(null);
  /** Admins and operator admins decide where credit may be spent; members only read it. */
  canEditCredit = signal(false);
  readonly totalCreditInCents = computed(() =>
    (this.wallets()?.holderWallets ?? []).reduce((sum, w) => sum + (w.balanceInCents ?? 0), 0));
  readonly totalCreditLabel = computed(() =>
    new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(this.totalCreditInCents() / 100));
  parkingRights = signal<ParkingRightDto[]>([]);
  transactions = signal<ParkingProcessListItemDto[]>([]);
  organizations = signal<OrganizationDto[]>([]);

  // ── Site Classification (per holder, per site) ────────────────────────────
  // The eligibility the tariff engine matches on (Student/Employee/…), scoped to a site. It hangs
  // off the parker, not the plate, so it is resolved once the plate's holders are known.
  siteClassifications = signal<ParkerSiteClassificationDto[]>([]);
  loadingClassifications = signal(false);

  /**
   * Cache per parker, so paging back to a plate — or switching between two plates of the same
   * customer — costs nothing. Dropped on an explicit refresh.
   */
  private classificationCache = new Map<string, ParkerSiteClassificationDto[]>();

  /**
   * Ceiling on the lookups one plate selection may fire. Classification is answered per parker, so
   * a plate with many holder records would otherwise fan out one request per row — and a plate
   * with more holders than this is the corrupted data the banners on this page already flag.
   */
  private static readonly MaxClassificationLookups = 5;

  // ── Ownership Reassignment Tool State ─────────────────────────────────────
  parkerQuery = signal('');
  parkerResults = signal<RegisteredParkerDto[]>([]);
  isSearchingParkers = signal(false);
  parkerSearched = signal(false);
  selectedParker = signal<RegisteredParkerDto | null>(null);
  isApplying = signal(false);
  lastResult = signal<ReassignLicensePlateOwnerResult | null>(null);

  // ── Edit Plate State ──────────────────────────────────────────────────────
  showEditModal = signal(false);
  isUpdatingPlate = signal(false);
  countries = PLATE_COUNTRIES;
  plateTypes: { value: LicensePlateType; label: string }[] = [
    { value: 'Personal', label: 'Personal' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Government', label: 'Government' },
    { value: 'Other', label: 'Other' }
  ];

  editForm = this.fb.group({
    fullNumber: ['', [Validators.required, Validators.minLength(3)]],
    countryCode: ['AT', Validators.required],
    type: ['Personal' as LicensePlateType, Validators.required]
  });

  // ── Feedback & Helpers ───────────────────────────────────────────────────
  copiedPlate = signal(false);
  spaceNamesMap = signal<Map<string, string>>(new Map());

  // ── Computed Holders ──────────────────────────────────────────────────────
  readonly holders = computed(() => this.ownership()?.holders ?? []);
  readonly registeredHolders = computed(() => this.holders().filter(h => h.isRegistered));
  readonly guestHolders = computed(() => this.holders().filter(h => !h.isRegistered));
  readonly isSettled = computed(() => this.ownership()?.isSettled === true);
  readonly canApply = computed(() =>
    !!this.ownership() && !!this.selectedParker()?.id && !this.isApplying()
  );

  readonly guestCollateralPlates = computed(() =>
    [...new Set(this.guestHolders().flatMap(h => h.otherPlates ?? []))]);

  readonly suspiciousGuests = computed(() =>
    this.guestHolders().filter(h => (h.otherPlates?.length ?? 0) >= 2));

  /**
   * The cars-inside caps on this plate's Dauerparker rights, one panel each. LongTerm only: other
   * rights can name an organization pool, which is not a parker cap. Capped at 3 so a plate with a
   * corrupted pile of rights cannot fan out into a request per right.
   */
  readonly capPoolIds = computed(() =>
    [...new Set(this.parkingRights()
      .filter(r => r.parkingType?.type?.type === ParkingTypeEnum.LongTerm)
      .map(r => r.poolParkingConfigId)
      .filter((id): id is string => !!id))]
      .slice(0, 3));

  // Ongoing stay check (currently parked vehicle)
  readonly ongoingStay = computed(() =>
    this.transactions().find(t => !t.endedAt && !!t.startedAt));

  /** The classifications in force right now — what the engine would match on this plate today. */
  readonly currentClassifications = computed(() =>
    this.siteClassifications().filter(c => this.isClassificationCurrent(c)));

  /**
   * Grouped per holder once, rather than filtering and sorting inside the holder rows: the template
   * asks for these on every change detection pass.
   */
  readonly classificationsByHolder = computed(() => {
    const byParker = new Map<string, ParkerSiteClassificationDto[]>();

    for (const c of this.siteClassifications()) {
      if (!c.parkerId) continue;
      const rows = byParker.get(c.parkerId);
      if (rows) rows.push(c); else byParker.set(c.parkerId, [c]);
    }

    for (const rows of byParker.values()) {
      rows.sort((a, b) =>
        Number(this.isClassificationCurrent(b)) - Number(this.isClassificationCurrent(a)));
    }

    return byParker;
  });

  ngOnInit(): void {
    this.roleService.checkAccess(Role.Administrator).then(isAdmin => {
      this.isAdministrator.set(isAdmin);
      if (isAdmin) this.canEditCredit.set(true);
      else this.roleService.checkAccess(Role.OperatorAdmin).then(isOpAdmin => this.canEditCredit.set(isOpAdmin));
      this.loadAccessibleSpaces();
    });
    this.loadBrowsePlates();
  }

  /**
   * Loads the spaces this operator is authorized to access.
   * The backend scopes this server-side based on the operator's JWT grants.
   */
  /** After an allowed-spaces edit: reload only the credit, not the whole plate. */
  reloadWallets(): void {
    const plateId = this.selectedPlate()?.id;
    if (!plateId) return;
    this.walletApi.operatorWalletByLicensePlateLicensePlateIdGet(plateId).pipe(
      catchError(() => of(null))
    ).subscribe(w => this.wallets.set(w));
  }

  private loadAccessibleSpaces(): void {
    this.spaceApi.parkingSpaceFilterPost(
      undefined, undefined, undefined, undefined, undefined, undefined, 0, 100, 'Name'
    ).subscribe({
      next: (result) => {
        const spaces = result?.items ?? [];
        this.accessibleSpaces.set(spaces);
        const map = new Map<string, string>();
        spaces.forEach(s => {
          if (s.id && s.name) map.set(s.id, s.name);
        });
        this.spaceNamesMap.set(map);
      },
      error: (err) => console.error('Error loading accessible spaces:', err)
    });
  }

  // ── Search & Browse ───────────────────────────────────────────────────────

  getPlateType(plate?: LicensePlateDto | null): string {
    if (!plate?.type || plate.type === 'NotSet' || (plate.type as any) === '0') {
      return 'Personal';
    }
    return plate.type;
  }

  searchPlates(): void {
    const query = this.plateQuery().trim();
    if (!query) {
      this.plateSearched.set(false);
      this.plateResults.set([]);
      return;
    }

    const normalized = normalizePlate(query);
    this.isSearchingPlates.set(true);
    this.plateSearched.set(true);
    this.selectedPlate.set(null);

    this.plateApi.operatorLicensePlateFilterPost({
      fullNumber: normalized,
      includeParkers: true,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 50
    }).subscribe({
      next: (page) => {
        const items = page?.items ?? [];
        this.plateResults.set(items);
        this.isSearchingPlates.set(false);

        // If exact single match, select it automatically
        if (items.length === 1 && items[0]) {
          this.selectPlate(items[0]);
        } else if (items.length > 0 && normalizePlate(items[0].fullNumber) === normalized) {
          this.selectPlate(items[0]);
        }
      },
      error: () => {
        this.plateResults.set([]);
        this.isSearchingPlates.set(false);
        this.snackBar.open('Failed to search license plates.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  clearSearch(): void {
    this.plateQuery.set('');
    this.plateSearched.set(false);
    this.plateResults.set([]);
  }

  clearSelectedPlate(): void {
    this.selectedPlate.set(null);
    this.ownership.set(null);
    this.parkingRights.set([]);
    this.transactions.set([]);
    this.organizations.set([]);
    this.siteClassifications.set([]);
    this.resetSelection();
  }

  loadBrowsePlates(): void {
    this.isBrowseLoading.set(true);
    this.plateApi.operatorLicensePlateFilterPost({
      includeParkers: true,
      includeDeleted: false,
      pageIndex: this.browsePageIndex(),
      pageSize: this.browsePageSize(),
      sortBy: 'CreatedAt',
      sortOrder: SortOrder.Descending
    }).subscribe({
      next: (page) => {
        this.browsePlates.set(page?.items ?? []);
        this.browseTotal.set(page?.totalCount ?? 0);
        this.isBrowseLoading.set(false);
      },
      error: () => {
        this.browsePlates.set([]);
        this.isBrowseLoading.set(false);
      }
    });
  }

  onBrowsePageChange(event: { pageIndex: number; pageSize: number }): void {
    this.browsePageIndex.set(event.pageIndex);
    this.browsePageSize.set(event.pageSize);
    this.loadBrowsePlates();
  }

  get browsePagination(): JJamesPaginationConfig {
    const total = this.browseTotal();
    const size = this.browsePageSize();
    return {
      currentPage: this.browsePageIndex(),
      totalPages: Math.max(1, Math.ceil(total / size)),
      pageSize: size,
      totalItems: total,
      itemsPerPage: size,
      pageSizeOptions: [10, 15, 30]
    };
  }

  // ── 360° Multi-Schema Enriched Data Loader ────────────────────────────────

  selectPlate(plate: LicensePlateDto): void {
    if (!plate || !plate.id) return;
    this.selectedPlate.set(plate);
    this.loadingEnriched.set(true);
    // Never let the previous plate's badges stand while this one loads.
    this.siteClassifications.set([]);
    this.wallets.set(null);
    this.resetSelection();

    const plateId = plate.id;
    const normalized = normalizePlate(plate.fullNumber);

    // Parallel multi-schema fetch with error resilience
    forkJoin({
      ownership: this.plateApi.operatorLicensePlateLicensePlateIdOwnershipGet(plateId).pipe(
        catchError(() => of(null))
      ),
      rights: this.rightApi.parkingRightsByLicensePlateLicensePlateIdGet(plateId, false, true).pipe(
        catchError(() => of([]))
      ),
      processes: this.processApi.parkingProcessFilterEnrichedPost({
        licensePlateId: plateId,
        includeDeleted: false,
        pageIndex: 0,
        pageSize: 50
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0 }))
      ),
      // One call per plate, whatever the ledger length: the backend resolves space names in bulk.
      wallets: this.walletApi.operatorWalletByLicensePlateLicensePlateIdGet(plateId).pipe(
        catchError(() => of(null))
      ),
      orgs: this.orgApi.organizationsFilterPost({
        pageIndex: 0,
        pageSize: 50,
        includeDeleted: false
      }).pipe(
        catchError(() => of({ items: [], totalCount: 0 }))
      )
    }).subscribe({
      next: ({ ownership, rights, processes, orgs, wallets }) => {
        // 1. Set Ownership & Holders
        this.ownership.set(ownership);
        this.wallets.set(wallets);

        // 1a. Site classifications hang off the holders, so they can only be asked for now.
        this.loadSiteClassifications(ownership?.holders ?? []);

        // 2. Filter Parking Rights with strict authorization rules
        const spaceIds = this.accessibleSpaceIds();
        const isAdmin = this.isAdministrator();

        const filteredRights = (rights ?? []).filter(r => {
          if (isAdmin) return true;
          // If right specifies spaces, at least one must be accessible to this operator
          if (r.allowedParkingSpaces && r.allowedParkingSpaces.length > 0) {
            return r.allowedParkingSpaces.some(s => s.parkingSpaceId && spaceIds.has(s.parkingSpaceId));
          }
          return true;
        });
        this.parkingRights.set(filteredRights);

        // 3. Filter Transactions / Stays strictly to accessible spaces
        const stayItems = processes?.items ?? [];
        const filteredStays = stayItems.filter(t => {
          if (isAdmin) return true;
          if (!t.parkingSpaceId) return false;
          return spaceIds.has(t.parkingSpaceId);
        });
        this.transactions.set(filteredStays);

        // 4. Filter Organizations and Pools associated with this plate
        const allOrgs = orgs?.items ?? [];
        const matchingOrgs = allOrgs.filter(org => {
          const orgSpaces = org.parkingSpaces ?? [];
          if (!isAdmin && orgSpaces.length > 0 && !orgSpaces.some(s => s.parkingSpaceId && spaceIds.has(s.parkingSpaceId))) {
            return false;
          }

          const hasPlateInPool = (org.poolParkConfigs ?? []).some(cfg =>
            (cfg.parkers ?? []).some(p =>
              (p.activeLicenses ?? []).some(l => normalizePlate(l.fullNumber) === normalized)
            )
          );
          const hasHolderInOrg = (ownership?.holders ?? []).some(h =>
            (org.parkers ?? []).some(p => p.parkerId === h.parkerId)
          );

          return hasPlateInPool || hasHolderInOrg;
        });
        this.organizations.set(matchingOrgs);

        this.loadingEnriched.set(false);
      },
      error: (err) => {
        console.error('Error loading enriched LP data:', err);
        this.loadingEnriched.set(false);
        this.snackBar.open('Could not load complete plate details.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  refreshCurrentPlate(): void {
    const plate = this.selectedPlate();
    // Drop the cache first: a classification changed elsewhere is exactly what a refresh is for.
    this.classificationCache.clear();
    this.poolCapStore.clear();
    if (plate) this.selectPlate(plate);
  }

  // ── Site Classification ───────────────────────────────────────────────────

  /**
   * Resolves each holder's classifications across every site in one request per parker — never one
   * per site — and only for holders already on screen.
   */
  private loadSiteClassifications(holders: LicensePlateHolderDto[]): void {
    const parkerIds = [...new Set(holders.map(h => h.parkerId).filter((id): id is string => !!id))]
      .slice(0, LicensePlateOwnershipComponent.MaxClassificationLookups);

    if (parkerIds.length === 0) {
      this.siteClassifications.set([]);
      return;
    }

    const missing = parkerIds.filter(id => !this.classificationCache.has(id));
    if (missing.length === 0) {
      this.publishClassifications(parkerIds);
      return;
    }

    this.loadingClassifications.set(true);
    forkJoin(
      missing.map(id => this.classificationApi.operatorParkerParkerIdSiteClassificationGet(id).pipe(
        // A parker with no classification is unclassified, not an error worth a snackbar.
        catchError(() => of([] as ParkerSiteClassificationDto[]))
      ))
    ).subscribe({
      next: (results) => {
        results.forEach((rows, i) => this.classificationCache.set(missing[i], rows ?? []));
        this.publishClassifications(parkerIds);
        this.loadingClassifications.set(false);
      },
      error: () => {
        this.loadingClassifications.set(false);
      }
    });
  }

  /** Narrows the cached rows to the spaces this operator may see, same rule as rights and stays. */
  private publishClassifications(parkerIds: string[]): void {
    const spaceIds = this.accessibleSpaceIds();
    const isAdmin = this.isAdministrator();

    this.siteClassifications.set(
      parkerIds
        .flatMap(id => this.classificationCache.get(id) ?? [])
        .filter(c => isAdmin || (!!c.parkingSpaceId && spaceIds.has(c.parkingSpaceId)))
    );
  }

  /** Classifications of one holder — the ones in force first, superseded and expired after. */
  classificationsForHolder(parkerId?: string | null): ParkerSiteClassificationDto[] {
    if (!parkerId) return [];
    return this.classificationsByHolder().get(parkerId) ?? [];
  }

  /** Only the currently active classifications for a holder — hides expired and superseded entries. */
  activeClassificationsForHolder(parkerId?: string | null): ParkerSiteClassificationDto[] {
    return this.classificationsForHolder(parkerId).filter(c => this.isClassificationCurrent(c));
  }

  /** i18n key for a classification chip — enum-driven, so a new value needs only a JSON key. */
  classificationLabelKey(classification?: ParkerSiteClassificationDto['classification']): string {
    return `classification.${classification ?? 'Unspecified'}`;
  }

  /**
   * Whether this row is the one the engine would match now. Mirrors the backend predicate:
   * active, started, and not yet ended.
   */
  isClassificationCurrent(classification: ParkerSiteClassificationDto): boolean {
    if (classification.isActive !== true) return false;

    const now = Date.now();
    if (classification.validFrom && new Date(classification.validFrom).getTime() > now) return false;
    if (classification.validTo && new Date(classification.validTo).getTime() <= now) return false;
    return true;
  }

  // ── Ownership Reassignment Tool ───────────────────────────────────────────

  chooseHolderAsOwner(holder: LicensePlateHolderDto): void {
    if (!holder.isRegistered) return;
    this.selectedParker.set({
      id: holder.parkerId,
      firstName: holder.firstName ?? undefined,
      lastName: holder.lastName ?? undefined,
      email: holder.email ?? undefined
    } as RegisteredParkerDto);
  }

  searchParkers(): void {
    const term = this.parkerQuery().trim();
    if (!term) return;

    this.isSearchingParkers.set(true);
    this.parkerSearched.set(true);

    this.parkerApi.operatorRegisteredParkerFilterPost({
      email: term,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 15
    }).subscribe({
      next: (page) => {
        this.parkerResults.set(page?.items ?? []);
        this.isSearchingParkers.set(false);
      },
      error: () => {
        this.parkerResults.set([]);
        this.isSearchingParkers.set(false);
        this.snackBar.open('Could not search parkers.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  selectParker(parker: RegisteredParkerDto): void {
    this.selectedParker.set(parker);
  }

  apply(): void {
    const plate = this.ownership();
    const target = this.selectedParker();
    if (!plate || !target?.id || this.isApplying()) {
      return;
    }

    const detaching = this.registeredHolders().filter(h => h.parkerId !== target.id);
    const lines = [
      `Make ${this.describeParker(target)} the sole owner of ${plate.fullNumber}?`,
      ''
    ];
    if (detaching.length) {
      lines.push(`Detaches: ${detaching.map(h => this.describeHolder(h)).join(', ')}`);
    }
    if (this.guestHolders().length) {
      lines.push(`Absorbs ${this.guestHolders().length} no-account record(s), moving their stays, violations and rights onto the account.`);
    }
    if (this.guestCollateralPlates().length) {
      lines.push(`Also moves these plates onto the account: ${this.guestCollateralPlates().join(', ')}`);
    }
    if (this.suspiciousGuests().length) {
      lines.push('', 'WARNING: a no-account record here holds three or more plates. Such a record is one vehicle '
        + '(at most a tractor and its trailer), so this is very likely bad data — absorbing it copies '
        + 'those plates onto a real account.');
    }
    lines.push('', 'Parking rights are not changed — other sites keep admitting this car.');

    if (!confirm(lines.join('\n'))) {
      return;
    }

    this.isApplying.set(true);
    this.plateApi.operatorLicensePlateLicensePlateIdOwnerPut(plate.licensePlateId!, {
      toRegisteredParkerId: target.id
    }).subscribe({
      next: result => {
        this.isApplying.set(false);
        this.lastResult.set(result);
        this.snackBar.open(`${plate.fullNumber} now belongs to ${this.describeParker(target)}.`, 'OK',
          { duration: 5000 });
        this.refreshCurrentPlate();
      },
      error: err => {
        this.isApplying.set(false);
        const detail = typeof err?.error === 'string' ? err.error : 'The change could not be applied.';
        this.snackBar.open(detail, 'Dismiss', { duration: 8000 });
      }
    });
  }

  describeHolder(holder: LicensePlateHolderDto): string {
    if (!holder.isRegistered) {
      return 'Guest / Unregistered Parker';
    }
    const name = `${holder.firstName ?? ''} ${holder.lastName ?? ''}`.trim();
    if (name && holder.email) {
      return `${name} (${holder.email})`;
    }
    return name || holder.email || 'Registered Account';
  }

  describeParker(parker: RegisteredParkerDto): string {
    const name = `${parker.firstName ?? ''} ${parker.lastName ?? ''}`.trim();
    if (name && parker.email) {
      return `${name} (${parker.email})`;
    }
    return name || parker.email || 'Registered Account';
  }

  private resetSelection(): void {
    this.selectedParker.set(null);
    this.parkerResults.set([]);
    this.parkerQuery.set('');
    this.parkerSearched.set(false);
    this.lastResult.set(null);
  }

  // ── Edit Plate Modal ──────────────────────────────────────────────────────

  openEditModal(): void {
    const plate = this.selectedPlate();
    if (!plate) return;
    this.editForm.patchValue({
      fullNumber: plate.fullNumber,
      countryCode: plate.countryCode ?? 'AT',
      type: plate.type ?? 'Personal'
    });
    this.showEditModal.set(true);
  }

  savePlateEdit(): void {
    const plate = this.selectedPlate();
    if (!plate?.id || this.editForm.invalid || this.isUpdatingPlate()) return;

    this.isUpdatingPlate.set(true);
    const raw = this.editForm.getRawValue();

    this.plateApi.operatorLicensePlateLicensePlateIdPut(plate.id, {
      fullNumber: normalizePlate(raw.fullNumber),
      countryCode: raw.countryCode ?? undefined,
      type: raw.type ?? 'Personal'
    }).subscribe({
      next: (updated) => {
        this.isUpdatingPlate.set(false);
        this.showEditModal.set(false);
        this.selectedPlate.set(updated);
        this.snackBar.open('License plate updated successfully.', 'Dismiss', { duration: 3000 });
        this.refreshCurrentPlate();
      },
      error: (err) => {
        this.isUpdatingPlate.set(false);
        const reason = err?.error?.detail || err?.error?.title || 'Could not update license plate.';
        this.snackBar.open(reason, 'Dismiss', { duration: 5000 });
      }
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  copyPlateNumber(): void {
    const num = this.selectedPlate()?.fullNumber;
    if (!num) return;
    navigator.clipboard.writeText(num).then(() => {
      this.copiedPlate.set(true);
      setTimeout(() => this.copiedPlate.set(false), 2000);
    });
  }

  getSpaceName(spaceId?: string): string {
    if (!spaceId) return 'Authorized Space';
    return this.spaceNamesMap().get(spaceId) ?? 'Authorized Space';
  }

  getRightValidity(right: ParkingRightDto): { from?: string | null; to?: string | null; isPermanent: boolean } {
    const rt = right.parkingRightType as any;
    const from = rt?.validFrom ?? null;
    const to = rt?.validTo ?? null;
    return { from, to, isPermanent: !from && !to };
  }

  getRightSchedule(right: ParkingRightDto): {
    timeWindow?: string | null;
    days?: string | null;
    isAllDay?: boolean;
    durationMinutes?: number | null;
    typeLabel?: string;
  } {
    const rt = right.parkingRightType as any;
    if (!rt) return { isAllDay: true, typeLabel: 'Standard', timeWindow: '24/7 (All-Day)', days: 'All Days' };

    if (rt.durationInMinutes !== undefined && rt.durationInMinutes !== null) {
      return {
        durationMinutes: rt.durationInMinutes,
        typeLabel: 'Duration-based',
        timeWindow: `${rt.durationInMinutes} min max stay`,
        days: 'All Days'
      };
    }

    const isAllDay = rt.isAllDay === true;
    let timeWindow: string | null = null;
    if (!isAllDay && (rt.startTime || rt.endTime)) {
      timeWindow = `${rt.startTime ?? '00:00'} — ${rt.endTime ?? '23:59'}`;
    } else if (isAllDay) {
      timeWindow = '24/7 (All-Day)';
    }
    const days = rt.validDays?.length ? rt.validDays.join(', ') : 'All Days';
    return { timeWindow, days, isAllDay, typeLabel: 'Time-based' };
  }

  /** A zone whose name did not resolve still shows, so a right never looks narrower than it is. */
  getRightZones(right: ParkingRightDto): string[] {
    return (right.allowedParkingZones ?? [])
      .map(z => z.zoneDto?.name || 'Unknown zone');
  }

  formatDuration(duration: string | null | undefined) {
    if (!duration) return 'N/A';
    // Duration is c# timespan string  1.03:54:56.9887360 , 09:29:15
    // contains days, hours, minutes, seconds, milliseconds. We want to display it human-readable.
    // Duration format: "d.hh:mm:ss.fffffff" or "hh:mm:ss" or "mm:ss"
    const parts = duration.split(':');
    if (parts.length === 3) {

      const daysAndHours = parts[0].split('.');
      let days = 0;
      let hours = 0;
      if (daysAndHours.length === 2) {
        days = parseInt(daysAndHours[0], 10);
        hours = parseInt(daysAndHours[1], 10);
      } else {
        hours = parseInt(daysAndHours[0], 10);
      }
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);
      return `${days > 0 ? `${days} Days ` : ''}${hours > 0 ? `${hours} Hours ` : ''}${minutes > 0 ? `${minutes} Minutes` : ''} ${seconds > 0 ? `${seconds} Seconds` : ''}`.trim();
    }

    return 'N/A';
  }

}

